/*
 * CB projects Ajax controller
 * ---------------------------
 * Shared by "CB: Projects Archive" (page) and "CB: Portfolio Showcase" (home).
 *
 * Each section server-renders only its first batch of cards. The grid element
 * carries data-next-url, which is Liquid's paginate.next.url for that batch.
 * IMPORTANT: metaobject pagination in Liquid is CURSOR based - a plain
 * ?page=2 is ignored by Shopify - so every batch has to be requested through
 * that cursor URL. We append the Section Rendering API parameter to it:
 *
 *     <paginate.next.url>&section_id=<section id>
 *
 * The response is the same section re-rendered one batch further on. We lift
 * the card nodes out of it and append them untouched, then read the response's
 * own data-next-url to know where the following batch lives. Because the cards
 * come from the same Liquid snippet as the server render, an Ajax card is
 * byte-identical to a server card and the design can never drift.
 *
 * Batches are also pulled eagerly during idle time, and immediately when the
 * visitor filters or searches, so client-side filtering describes the whole
 * list rather than the visible batch.
 *
 * No regular expressions are used on purpose: the data attributes are already
 * normalised in Liquid, and plain string ops keep this file safe to hand-edit.
 */
(function () {
  'use strict';

  function tokens(value, separator) {
    var parts = String(value == null ? '' : value)
      .toLowerCase()
      .split(separator);
    var kept = [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part) kept.push(part);
    }
    return kept;
  }

  function norm(value) {
    return tokens(value, ' ').join(' ');
  }

  /**
   * @param {Object} config
   *  root          - section element
   *  cardSelector  - CSS selector for one card
   *  hideMode      - 'class' (toggles hiddenClass) or 'style' (display none)
   *  hiddenClass   - class used when hideMode === 'class'
   *  insertBefore  - optional selector inside the grid to insert batches before
   *  onRender      - called after every batch
   *  onAllLoaded   - called once the whole list is in the DOM
   */
  function CBProjectsAjax(config) {
    var root = config.root;
    var grid = root.querySelector('[data-cb-grid]');
    if (!grid) return null;

    var api = {};
    var sectionId = grid.getAttribute('data-section-id');
    var nextUrl = (grid.getAttribute('data-next-url') || '').trim();

    var seen = {};
    var pending = null;

    api.grid = grid;
    api.cards = [];
    api.allLoaded = !nextUrl;
    api.perPage = parseInt(grid.getAttribute('data-per-page'), 10) || 12;
    api.totalItems = parseInt(grid.getAttribute('data-total-items'), 10) || 0;

    api.norm = norm;
    api.tokens = tokens;

    api.refresh = function () {
      api.cards = Array.prototype.slice.call(grid.querySelectorAll(config.cardSelector));
      api.cards.forEach(function (card) {
        var href = card.getAttribute('href');
        if (href) seen[href] = true;
      });
      return api.cards;
    };

    api.searchOf = function (card) {
      if (card.cbSearch == null) card.cbSearch = norm(card.getAttribute('data-search'));
      return card.cbSearch;
    };

    api.listOf = function (card, attribute) {
      var key = 'cbList_' + attribute;
      if (!card[key]) card[key] = tokens(card.getAttribute(attribute), '|');
      return card[key];
    };

    api.show = function (card) {
      if (config.hideMode === 'style') {
        card.style.display = '';
      } else {
        card.classList.remove(config.hiddenClass);
      }
    };

    api.hide = function (card) {
      if (config.hideMode === 'style') {
        card.style.display = 'none';
      } else {
        card.classList.add(config.hiddenClass);
      }
    };

    function busy(state) {
      if (state) {
        root.setAttribute('data-cb-loading', '1');
      } else {
        root.removeAttribute('data-cb-loading');
      }
    }

    function withSectionId(url) {
      if (!sectionId) return url;
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
    }

    function fetchBatch() {
      var url = withSectionId(nextUrl);

      return fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incomingGrid = doc.querySelector('[data-cb-grid]');
          var incoming = doc.querySelectorAll(config.cardSelector);

          var frag = document.createDocumentFragment();
          var added = 0;

          Array.prototype.forEach.call(incoming, function (card) {
            var href = card.getAttribute('href');
            if (href && seen[href]) return;
            if (href) seen[href] = true;
            var node = document.importNode(card, true);
            api.hide(node);
            frag.appendChild(node);
            added++;
          });

          if (added) {
            var anchor = config.insertBefore ? grid.querySelector(config.insertBefore) : null;
            if (anchor && anchor.parentNode === grid) {
              grid.insertBefore(frag, anchor);
            } else {
              grid.appendChild(frag);
            }
            api.refresh();
          }

          // Advance the cursor. No next url, or a url we already used, means
          // we are at the end of the list.
          var following = incomingGrid ? (incomingGrid.getAttribute('data-next-url') || '').trim() : '';
          if (!following || following === nextUrl || !added) {
            nextUrl = '';
            api.allLoaded = true;
          } else {
            nextUrl = following;
          }

          return added;
        });
    }

    /** Load exactly one more batch. */
    api.loadNext = function () {
      if (api.allLoaded || pending) return Promise.resolve(0);
      busy(true);
      pending = fetchBatch()
        .catch(function (err) {
          if (window.console && window.console.warn) window.console.warn('[CB projects] batch failed', err);
          return 0;
        })
        .then(function (added) {
          pending = null;
          busy(false);
          if (api.allLoaded && config.onAllLoaded) config.onAllLoaded();
          if (config.onRender) config.onRender();
          return added;
        });
      return pending;
    };

    /** Load every remaining batch, one request at a time. */
    api.loadAll = function () {
      if (api.allLoaded) return Promise.resolve();
      if (pending) return pending.then(api.loadAll);

      busy(true);

      var guard = 0;
      var step = function () {
        if (api.allLoaded || !nextUrl) return Promise.resolve();
        if (++guard > 50) return Promise.resolve();
        return fetchBatch().then(function () {
          if (config.onRender) config.onRender();
          return step();
        });
      };

      pending = step()
        .catch(function (err) {
          if (window.console && window.console.warn) window.console.warn('[CB projects] batch failed', err);
        })
        .then(function () {
          pending = null;
          busy(false);
          if (api.allLoaded && config.onAllLoaded) config.onAllLoaded();
          if (config.onRender) config.onRender();
        });

      return pending;
    };

    /** Pull the rest of the list once the browser is idle. */
    api.warm = function () {
      if (api.allLoaded) return;
      var run = function () {
        api.loadAll();
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        window.setTimeout(run, 800);
      }
    };

    api.refresh();
    return api;
  }

  window.CBProjectsAjax = CBProjectsAjax;
})();
